<template>
    <div>
        <div class="vue-map-container w-100" :style="'min-height: ' + height + 'px'">
            <div ref="vue-map" class="vue-map"></div>
            <div class="vue-map-hidden">
                <slot></slot>
            </div>
            <slot name="visible"></slot>
        </div>
    </div>
</template>

<script>
const recyclePrefix = '__gmc__'

export default {
	components: {},
	props: {
        value: null,
        center: {
            required: true,
            type: Object,
        },
        startLocation: Object,
        zoom: {
            required: false,
            type: Number,
        },
        heading: {
            type: Number,
        },
        mapTypeId: {
            type: String
        },
        tilt: {
            type: Number
        },
        height: {
            type: String,
            default: "500"
        },
        options: {
            type: Object,
            default () { return {} }
        },
        // Ordered stops for the day being planned - drawn as numbered markers joined by a line from the start location
        route: {
            type: Array,
            default () { return [] }
        },
        // Group nearby markers into count bubbles when zoomed out
        cluster: {
            type: Boolean,
            default: false
        },
        clusterMaxZoom: {
            type: Number,
            default: 11
        }
    },
	data: () => ({
        $mapObject: null,
        markers: [],
        clusterMarkers: [],
        routeMarkers: [],
        routePolyline: null,
        startMarker: null
    }),
	computed: {
        tasks: {
            get() {
                if (!this.value) {
                    return []
                }
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        }
    },
    watch: {
        zoom (zoom) {
            if (this.$mapObject) {
                this.$mapObject.setZoom(zoom)
            }
        },
        tasks: {
            handler(val) {
                this.addMarkers(val)
            },
            deep: true
        },
        startLocation(val) {
            if (this.startMarker) {
                this.startMarker.setMap(null)
            }
            this.setStartMarker(val)
            this.setRoute(this.route)
        },
        route(val) {
            this.setRoute(val)
        }
    },
	created() {},
	mounted() {
        this.$gmapApiPromise().then(() => {
            // getting the DOM element where to create the map
            const element = this.$refs['vue-map']

            // creating the map
            var options = {
                zoom: this.zoom,
                center: this.center,
                mapTypeId: this.mapTypeId,
                ...this.options,
                styles: [
                    {
                        featureType: "poi",
                        stylers: [{ visibility: "off" }],
                    },
                ]
            }

            const recycleKey = this.getRecycleKey()

            if (this.options.recycle && window[recycleKey]) {
                element.appendChild(window[recycleKey].div)
                this.$mapObject = window[recycleKey].map
                this.$mapObject.setOptions(options)
            } else {
                this.$mapObject = new google.maps.Map(element, options)
                window[recycleKey] = { map: this.$mapObject }
            }

            this.$mapObject.addListener('zoom_changed', () => {
                this.$emit('zoom_changed', this.$mapObject.getZoom())
                this.renderClusters()
            })

            this.$mapObject.addListener('bounds_changed', () => {
                this.$emit('bounds_changed', this.$mapObject.getBounds())
            })

            this.$mapObject.addListener('idle', () => {
                this.$emit('idle', this.$mapObject.getBounds())
            })

            if (this.startLocation) {
                this.setStartMarker(this.startLocation)
            }

            if (this.tasks.length > 0) {
                this.addMarkers(this.tasks)
            }

            if (this.route.length > 0) {
                this.setRoute(this.route)
            }

            return this.$mapObject
        })
        .catch(error => {

        })
    },
    beforeDestroy () {
        const recycleKey = this.getRecycleKey()
        if (window[recycleKey]) {
            window[recycleKey].div = this.$mapObject.getDiv()
        }
    },
	methods: {
        getRecycleKey () {
            return this.options.recycle ? recyclePrefix + this.options.recycle : recyclePrefix
        },
        setStartMarker(location) {
            if (location) {
                var icon = {
                    url: require('@/assets/images/icons/home-marker.png'),
                    scaledSize: new google.maps.Size(25, 34),
                }

                var marker = new google.maps.Marker({
                    position: location,
                    map: this.$mapObject,
                    icon: icon,
                    zIndex: 9999
                });

                this.startMarker = marker
            } else {
                this.startMarker.setMap(null)
                this.startMarker = null
            }
        },
        search() {
            var geocoder = new google.maps.Geocoder();

            geocoder.geocode({ address: this.city, region: "DK" }, (results, status) => {
                if (status == 'OK') {
                    this.$mapObject.setCenter(results[0].geometry.location);

                    var bounds = results[0].geometry.bounds
                    var northEast = bounds.getNorthEast()
                    var southWest = bounds.getSouthWest()

                    var path = [
                        { lat: northEast.lat(), lng: southWest.lng() },
                        { lat: northEast.lat(), lng: northEast.lng() },
                        { lat: southWest.lat(), lng: northEast.lng() },
                        { lat: southWest.lat(), lng: southWest.lng() },
                    ];

                    this.addPath(path)
                }
            });


        },
        addMarkers(obj) {

            this.markers.forEach(marker => {
                marker.setMap(null)
            })
            this.markers = []

            obj.forEach(task => {

                // require('@/assets/images/icons/home-marker.png')
                // require('@/assets/images/icons/task-marker.png')

                var icon = {
                    url: require('@/assets/images/icons/task-marker.png'),
                    scaledSize: new google.maps.Size(25, 34),
                }

                var planned = false;
                if (task.planning) {
                    planned = task.planning.length > 0;
                }
                if (task.schedule) {
                    planned = task.schedule.length > 0;
                }

                var zIndex = 10;

                if (planned) {
                    icon.url = require('@/assets/images/icons/planned2-marker.png')
                    zIndex = 1
                }

                var marker = new google.maps.Marker({
                    position: { lng: task.location.coordinates[0], lat: task.location.coordinates[1] },
                    map: this.cluster ? null : this.$mapObject,
                    title: task.address,
                    id: task.id,
                    icon: icon,
                    zIndex: zIndex
                });
                marker.baseIcon = icon.url
                marker.baseZIndex = zIndex

                marker.addListener("click", () => {
                    this.$emit('click_marker', marker.id)
                });

                marker.addListener("mouseover", () => {
                    this.$emit('mouseover_marker', marker.id)
                });
                marker.addListener("mouseout", () => {
                    this.$emit('mouseout_marker', marker.id)
                });

                this.markers.push(marker)

            })

            this.renderClusters()
        },
        // Simple grid clustering: bucket markers by a 60px grid at the current zoom and replace
        // buckets with more than one marker by a count bubble. Click a bubble to zoom in on it.
        renderClusters() {
            if (!this.cluster || !this.$mapObject) {
                return
            }
            var projection = this.$mapObject.getProjection()
            if (!projection) {
                return
            }

            this.clusterMarkers.forEach(marker => marker.setMap(null))
            this.clusterMarkers = []

            this.markers.forEach(marker => marker.clusterMarker = null)

            var zoom = this.$mapObject.getZoom()
            if (zoom >= this.clusterMaxZoom) {
                this.markers.forEach(marker => marker.setMap(this.$mapObject))
                return
            }

            var scale = Math.pow(2, zoom)
            var grid = 60
            var buckets = {}
            this.markers.forEach(marker => {
                var point = projection.fromLatLngToPoint(marker.getPosition())
                var key = Math.floor(point.x * scale / grid) + "_" + Math.floor(point.y * scale / grid)
                if (!buckets[key]) {
                    buckets[key] = []
                }
                buckets[key].push(marker)
            })

            Object.values(buckets).forEach(group => {
                if (group.length < 2) {
                    group[0].setMap(this.$mapObject)
                    return
                }

                var lat = 0, lng = 0
                group.forEach(marker => {
                    marker.setMap(null)
                    lat += marker.getPosition().lat()
                    lng += marker.getPosition().lng()
                })
                var position = { lat: lat / group.length, lng: lng / group.length }

                var icon = {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 14 + Math.min(group.length, 60) / 5,
                    fillColor: "#3279d0",
                    fillOpacity: 0.9,
                    strokeColor: "#fff",
                    strokeWeight: 2
                }

                var clusterMarker = new google.maps.Marker({
                    position,
                    map: this.$mapObject,
                    label: { text: String(group.length), color: "#fff", fontWeight: "bold", fontSize: "12px" },
                    icon: icon,
                    zIndex: 50
                })
                clusterMarker.baseIcon = icon
                clusterMarker.addListener("click", () => {
                    this.$mapObject.setCenter(position)
                    this.$mapObject.setZoom(Math.min(zoom + 2, this.clusterMaxZoom))
                })
                group.forEach(marker => marker.clusterMarker = clusterMarker)
                this.clusterMarkers.push(clusterMarker)
            })
        },
        setRoute(stops) {
            this.routeMarkers.forEach(marker => marker.setMap(null))
            this.routeMarkers = []
            if (this.routePolyline) {
                this.routePolyline.setMap(null)
                this.routePolyline = null
            }

            if (!this.$mapObject || !stops || stops.length == 0) {
                return
            }

            var path = []
            if (this.startLocation) {
                path.push(this.startLocation)
            }

            stops.forEach((stop, idx) => {
                var position = { lat: stop.lat, lng: stop.lng }
                path.push(position)

                var marker = new google.maps.Marker({
                    position,
                    map: this.$mapObject,
                    title: stop.title,
                    label: { text: String(idx + 1), color: "#fff", fontWeight: "bold", fontSize: "12px" },
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 12,
                        fillColor: stop.color || "#28c76f",
                        fillOpacity: 1,
                        strokeColor: "#fff",
                        strokeWeight: 2
                    },
                    zIndex: 20000
                })
                this.routeMarkers.push(marker)
            })

            if (this.startLocation) {
                path.push(this.startLocation)
            }

            this.routePolyline = new google.maps.Polyline({
                path,
                map: this.$mapObject,
                geodesic: true,
                strokeColor: "#28c76f",
                strokeOpacity: 0.9,
                strokeWeight: 3
            })
        },
        fitRoute() {
            if (!this.$mapObject || this.routeMarkers.length == 0) {
                return
            }
            var bounds = new google.maps.LatLngBounds()
            if (this.startLocation) {
                bounds.extend(this.startLocation)
            }
            this.routeMarkers.forEach(marker => bounds.extend(marker.getPosition()))
            this.$mapObject.fitBounds(bounds, 60)
        },
        panTo(id) {
            var marker = this.markers.find(x => x.id == id)
            if (marker) {
                this.$mapObject.panTo(marker.getPosition())
            }
        },
        highlightMarker(id, hover) {
            var marker = this.markers.find(x => x.id == id)
            if (marker && marker.clusterMarker) {
                // Marker is folded into a cluster bubble - highlight the bubble instead
                var cluster = marker.clusterMarker
                var clusterIcon = { ...cluster.baseIcon }
                if (hover) {
                    clusterIcon.fillColor = "#ea5455"
                    clusterIcon.scale = cluster.baseIcon.scale + 4
                }
                cluster.setIcon(clusterIcon)
                cluster.setZIndex(hover ? 99999 : 50)
                return
            }
            if (marker) {
                var icon = {
                    url: require('@/assets/images/icons/highlight-marker.png'),
                    scaledSize: new google.maps.Size(25, 34),
                }
                var zIndex = 99999
                if (!hover) {
                    icon.url = marker.baseIcon
                    zIndex = marker.baseZIndex
                }
                marker.setIcon(icon)
                marker.setZIndex(zIndex)
            }
        }
    },
};
</script>


<style lang="css">
    .vue-map-container {
        position: relative;
    }
    .vue-map-container .vue-map {
        left: 0; right: 0; top: 0; bottom: 0;
        position: absolute;
    }
    .vue-map-hidden {
        display: none;
    }
</style>